import React, { useContext } from 'react'
import copy from 'copy-to-clipboard'
import moment from 'moment'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import CopyIcon from 'transfix-icons/build/CopyIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Box, Button, makeStyles } from 'transfix-ui/core'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ShareLoadContext } from 'apps/fleet-planner/context'
import * as Types from 'apps/fleet-planner/types'
import { RemoveIcon } from 'transfix-icons'
import { Tooltip, Avatar, TAG_SIZE, TAG_STYLE, Tag, brandColors } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'

interface IShareLoadTableRowProps {
  drivers?: Types.IDriver[]
  share: Types.IShare
}

const useStyles = makeStyles({
  deactivateButton: {
    color: brandColors.coolGray5,
  },
  copyLink: {
    color: brandColors.skyBlue6,
  },
  unassignedDriver: {
    marginRight: '150px',
  },
})

export const ShareLoadTableRow: React.FC<IShareLoadTableRowProps> = ({ drivers, share }) => {
  const {
    sharedWithDriver,
    lastSharedAt,
    lastSharedVia = [],
    id: shareId,
    shareableId: shipmentId,
    url = '',
  } = share || {}
  const classes = useStyles()
  const { firstName = '', id, lastName = '' } = sharedWithDriver || {}
  const { deleteShare, pushNewToastMessage } = useContext(ShareLoadContext)
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFmsOneTimeSmsLoadShareEnabled } = getFlag('fmsOneTimeSmsLoadShare')
  const assignedDriver = drivers?.some(driver => driver.id === id)

  /**
   * Deletes share from shipment
   */
  const deleteShareHandler = async () => {
    if (!shareId) return
    try {
      await deleteShare(shipmentId, shareId)
      pushNewToastMessage('Driver’s access removed')
    } catch (e) {
      // no op
    }
  }

  /**
   * Formats the string to contain different syntax
   * depending on relative difference in time
   * @param {string} isoString
   * @example 'Today at 7:00pm'
   */
  const formatLastShared = (isoString: string) => {
    const timezone = moment.tz.guess(true)
    return moment(isoString)
      .tz(timezone)
      .calendar(isFmsOneTimeSmsLoadShareEnabled ? isoString : undefined, {
        sameDay: isFmsOneTimeSmsLoadShareEnabled
          ? FORMATS.datetimeFormat
          : `[Today] [at] ${FORMATS.timeFormatShort12H}`,
        lastDay: isFmsOneTimeSmsLoadShareEnabled
          ? FORMATS.datetimeFormat
          : `[Yesterday] [at] ${FORMATS.timeFormatShort12H}`,
        sameElse: `ddd ${FORMATS.readableFormat} [at] ${FORMATS.datetimeFormat}`,
      })
  }

  // Computed values
  const fullName = `${firstName} ${lastName}`.trim()
  const lastSharedAtFormatted = formatLastShared(lastSharedAt!)

  const sharedViaFormats: Record<Types.ShareViaType, string> = {
    [Types.ShareViaType.sms]: 'text',
    [Types.ShareViaType.email]: 'email',
  }

  const lastSharedViaFormatted = lastSharedVia?.map(t => sharedViaFormats[t])?.join(' and ')

  const copyTextHandler = () => {
    copy(url)
    pushNewToastMessage('Link copied to clipboard')
  }

  return (
    <div className='share-load-table-row fadeInLeft mb3' data-test='share-load-table-row'>
      {/* Avatar - fixed width */}
      <div className='share-load-table-row-avatar'>
        <Avatar name={fullName} size='medium' />
      </div>

      {/* Name, last shared, etc. - expanding width */}
      <div className='share-load-table-row-name'>
        <div className='b ellipsis'>{fullName}</div>
        {lastSharedAtFormatted && lastSharedViaFormatted && (
          <div>
            <span className='dib'>
              {isFmsOneTimeSmsLoadShareEnabled
                ? lastSharedAtFormatted
                : `Last Shared ${lastSharedAtFormatted}`}
            </span>
            <span className='dib ml1 mr1'>&middot;</span>
            <span className='dib text-secondary'>Sent {lastSharedViaFormatted}</span>
          </div>
        )}
      </div>

      {/* Unassigned Driver - fixed */}
      {isFmsOneTimeSmsLoadShareEnabled && id && (
        <div className={classes.unassignedDriver}>
          <span className='dib'>{!assignedDriver ? 'Unassigned Driver' : 'Assigned Driver'}</span>
        </div>
      )}

      {/* Tag - fixed */}
      {!isFmsOneTimeSmsLoadShareEnabled && (
        <div className='share-load-table-row-tag'>
          <Tag label='Basic load info only' size={TAG_SIZE.small} tagStyle={TAG_STYLE.info} />
        </div>
      )}

      {/* Actions - fixed */}
      <div className='share-load-table-row-actions'>
        {isFmsOneTimeSmsLoadShareEnabled && (
          <Box className={classes.deactivateButton}>
            <Button
              startIcon={<RemoveIcon />}
              variant='text'
              onClick={deleteShareHandler}
              data-test='remove-share-button'>
              Deactivate Link
            </Button>
          </Box>
        )}

        {isFmsOneTimeSmsLoadShareEnabled ? (
          <Box className={classes.copyLink}>
            <Button
              startIcon={<CopyIcon />}
              variant='text'
              onClick={copyTextHandler}
              data-test='copy-share-button'>
              Copy Link
            </Button>
          </Box>
        ) : (
          <Box className='flex' onClick={copyTextHandler} role='button'>
            <Tooltip
              id='copy-share'
              position='top'
              effect='solid'
              trigger={
                <IconButton
                  onClick={copyTextHandler}
                  Icon={CopyIcon}
                  label='Copy URL'
                  color='tertiary'
                  data-test='copy-share-button'
                  size='large'
                />
              }>
              Copy Link
            </Tooltip>
          </Box>
        )}
        {!isFmsOneTimeSmsLoadShareEnabled && (
          <div className='flex'>
            <IconButton
              onClick={deleteShareHandler}
              Icon={CloseIcon}
              label='Remove Share'
              color='tertiary'
              data-test='remove-share-button'
              size='large'
            />
          </div>
        )}
      </div>
    </div>
  )
}
